import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Tag } from './tagsTypes'

export interface TagsState {
  allTags: Tag[] | null
  tagsMap: Dictionary<Tag> | null
  tagUpdateFlag: string | null
  tagCreateFlag: string | null
  tagEmployeesUpdateSeq: number
}

const initialState: TagsState = {
  allTags: null,
  tagsMap: null,
  tagUpdateFlag: null,
  tagCreateFlag: null,
  tagEmployeesUpdateSeq: 0,
}

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    getTags: () => {},
    createTag: (_state, _action: PayloadAction<{ name: string }>) => {},
    updateTag: (
      _state,
      _action: PayloadAction<{ tagId: string; name: string }>
    ) => {},
    deleteTag: (_state, _action: PayloadAction<{ tagId: string }>) => {},
    addTagEmployees: (
      _state,
      _action: PayloadAction<{ tagId: string; employees: string[] }>
    ) => {},
    removeTagEmployees: (
      _state,
      _action: PayloadAction<{ tagId: string; employees: string[] }>
    ) => {},
    toggleTagUpdateFlag: (state, action: PayloadAction<string | undefined>) => {
      state.tagUpdateFlag = action.payload ?? null
    },
    toggleTagCreateFlag: (state, action: PayloadAction<string | undefined>) => {
      state.tagCreateFlag = action.payload ?? null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTagsSuccess, (state, action: PayloadAction<Tag[]>) => {
      state.allTags = action.payload
      state.tagsMap = action.payload.reduce((acc, tag) => {
        acc[tag.id] = tag
        return acc
      }, {} as Dictionary<Tag>)
    })
    builder.addCase(createTagSuccess, (state, action: PayloadAction<Tag>) => {
      if (state.allTags) {
        state.allTags = [...state.allTags, action.payload]
      } else {
        state.allTags = [action.payload]
      }
      if (state.tagsMap) {
        state.tagsMap[action.payload.id] = action.payload
      } else {
        state.tagsMap = { [action.payload.id]: action.payload }
      }
      state.tagCreateFlag = action.payload.id
    })
    builder.addCase(updateTagSuccess, (state, action: PayloadAction<Tag>) => {
      if (state.allTags) {
        state.allTags = state.allTags.map((tag) =>
          tag.id === action.payload.id ? action.payload : tag
        )
      }
      if (state.tagsMap) {
        state.tagsMap[action.payload.id] = action.payload
      }
      state.tagUpdateFlag = action.payload.id
    })
    builder.addCase(
      deleteTagSuccess,
      (state, action: PayloadAction<{ tagId: string }>) => {
        if (state.allTags) {
          state.allTags = state.allTags.filter(
            (tag) => tag.id !== action.payload.tagId
          )
        }
        if (state.tagsMap) {
          delete state.tagsMap[action.payload.tagId]
        }
        state.tagUpdateFlag = action.payload.tagId
      }
    )
    builder.addCase(
      updateTagEmployeesSuccess,
      (state, action: PayloadAction<Tag>) => {
        if (state.allTags) {
          state.allTags = state.allTags.map((tag) =>
            tag.id === action.payload.id ? action.payload : tag
          )
        }
        if (state.tagsMap) {
          state.tagsMap[action.payload.id] = action.payload
        }
        state.tagUpdateFlag = action.payload.id
        state.tagEmployeesUpdateSeq += 1
      }
    )
  },
})

export const getTagsSuccess = createAction<Tag[]>('tags/getTagsSuccess')
export const createTagSuccess = createAction<Tag>('tags/createTagSuccess')
export const updateTagSuccess = createAction<Tag>('tags/updateTagSuccess')
export const deleteTagSuccess = createAction<{ tagId: string }>(
  'tags/deleteTagSuccess'
)
export const updateTagEmployeesSuccess = createAction<Tag>(
  'tags/updateTagEmployeesSuccess'
)

export const tagsActions = tagsSlice.actions
export const tagsReducer = tagsSlice.reducer
